import {
  fromDate,
  type ZonedDateTime,
} from '@internationalized/date'
import type { paths as ApiPaths } from '#open-fetch-schemas/api'

export function useFormHelper<
  T extends keyof ApiPaths,
  M extends keyof ApiPaths[T],
  DateFields extends readonly string[] = [],
>(
  {
    initialState,
    saveEndpoint,
    afterSave,
    handleResponseUsing,
    notifyMessage,
    errorMessage,
    beforeSave,
    path,
    method = 'post' as M,
    errorPrefix = null,
    dateFields = [] as unknown as DateFields,
  }: {
    initialState: ApiPaths[T][M] extends {
      requestBody: { content: { 'application/json': infer U } }
    } ? Partial<U> : never
    saveEndpoint: keyof ApiPaths
    afterSave?: (state: ApiPaths[T][M] extends {
      responses: {
        200: { content: { 'application/json': infer U } }
      }
    } ? U : never) => void
    handleResponseUsing?: (state: ApiPaths[T][M] extends {
      responses: { 200: { content: { 'application/json': infer U } } }
    } ? U : never) => void
    notifyMessage?: string
    errorMessage?: string
    beforeSave?: (state: typeof initialState) => typeof initialState
    path: ApiPaths[T][M] extends {
      parameters: { path: infer U }
    } ? U : undefined
    method?: M
    errorPrefix?: string | null
    dateFields?: DateFields
  },
) {
  const { $ts: t, $api } = useNuxtApp()

  const state = ref<typeof initialState>(initialState)
  const isSaving = ref(false)
  const didSave = ref(false)

  type FormStateType = ApiPaths[T][M] extends {
    requestBody: { content: { 'application/json': infer U } }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } ? U extends Record<string, any>
      ? {
          [K in keyof U]: K extends DateFields[number]
            ? ZonedDateTime
            : U[K]
        }
      : never
    : never

  watch(state, () => {
    didSave.value = false
  }, { deep: true })

  function getInitialValues(): FormStateType {
    // This takes the initialState, converts all the dateFields to ZonedDateTime
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const initialValues = Object.assign({}, initialState) as unknown as Record<string, any>
    const initialStateRecord = initialState as Record<string, unknown>

    for (const dateField of dateFields) {
      if (initialStateRecord[dateField]) {
        // Convert date string to ZonedDateTime if needed
        const value = initialStateRecord[dateField]
        if (typeof value === 'string') {
          try {
            initialValues[dateField] = fromDate(new Date(value), 'UTC')
          }
          catch {
            // Keep the original value if conversion fails
            console.warn(`Failed to convert ${dateField} to ZonedDateTime`)
          }
        }
      }
    }
    return initialValues as FormStateType
  }

  type FormState = FormStateType

  function convertDates(state: FormState): typeof initialState {
    const stateRecord = state as Record<string, unknown>
    for (const dateField of dateFields) {
      if (stateRecord[dateField]) {
        stateRecord[dateField] = (stateRecord[dateField] as ZonedDateTime).toDate().toISOString()
      }
    }
    return state as typeof initialState
  }

  const { handleSubmit, setErrors, values, defineField, setValues, setFieldValue, resetForm } = useForm<FormStateType>({
    initialValues: getInitialValues(),
  })

  function notify() {
    if (notifyMessage) {
      useSonner.success(notifyMessage)
    }
    else {
      useSonner.success(t('Saved'))
    }
  }

  const submit = handleSubmit((values) => {
    if (state.value === undefined) {
      throw new Error('State is undefined')
    }

    isSaving.value = true

    const stateToSave = beforeSave ? beforeSave(convertDates(values)) : convertDates(values)

    $api(saveEndpoint, {
      method: method as 'post' | 'put' | 'patch',
      // @ts-expect-error not sure why this is needed
      path: path,
      // @ts-expect-error not sure why this is needed
      body: stateToSave,
    }).then((data) => {
      if (handleResponseUsing !== undefined) {
        state.value = handleResponseUsing(data)
      }
      else {
        state.value = data
      }

      afterSave?.(data)

      nextTick(() => {
        didSave.value = true
        isSaving.value = false
        notify()
      })
    }).catch((e) => {
      // If e.data.errors is set, prefix them all with "config" before setting them.
      if (e.data && e.data.errors) {
        if (errorPrefix) {
          const original = e.data.errors
          e.data.errors = Object.fromEntries(Object.entries(e.data.errors).map(([key, value]) => [`${errorPrefix}.${key}`, value]))
          // Also add the original errors to the errors object.
          e.data.errors = {
            ...e.data.errors,
            ...original,
          }
        }
      }
      else {
        console.error(e)
        e.data.errors = null
      }

      if (errorMessage) {
        useSonner.error(errorMessage)
      }
      else {
        useSonner.error(t('Failed to save'), {
          description: e.data?.message ?? t('An unknown error occurred'),
        })
      }
      setErrors(e.data.errors)
    }).finally(() => {
      isSaving.value = false
    })
  })

  return {
    state: values,
    isSaving,
    didSave,
    submit,
    defineField,
    setValues,
    setFieldValue,
    resetForm,
  }
}
